import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, EmptyState, Note, PageHeader, Segmented, Skeleton, Tile, Tiles } from '@/ui'
import { testDone, useCampaigns, useCampaignTest, useLocations, useNotifications, useOverview, useSupports, type ActionItem } from '@/lib/api'
import { getWheelDesign } from '@/lib/wheel'
import { allCampaigns, campaignHasGift, getCampaigns, getDestinations, getFiche, mergeSupports, servedBy } from '@/lib/campaign'
import { readiness } from '@/lib/readiness'
import { ALL, currentUser, useScope } from '@/lib/scope'
import { useUnreadMessages } from '@/lib/messages'
import { factsFor, quandParis, quotaFact } from '@/lib/notifications'
import { useTillDevices } from '@/routes/parametres/api'
import { DemoBanner } from '@/routes/resultats/DemoBanner'
import { getLedger } from '@/routes/cadeaux/Ledger'
import type { LedgerRow } from '@/mocks/data-b'
import { currentWorld } from '@/mocks/worlds'

/* Accueil — R1 (setup spec MA-1/2/3, merchant-experience § C and wireframe 1, both ACCEPTED 08/10;
   Lyam « overall go » 12:39). Day one: the next guide step, large, with its progress, and the till
   door; no tile before it has a source. Then: one thing to do this week, four real numbers, the till
   door. No explanation band, one primary per screen.
   v3.3 migration · pack accueil (WF-001 · WF-051): « Activité du salon » reads the participation ledger the app
   already has (routes/cadeaux/Ledger `getLedger`, the same rows Résultats draws) over 7 or 30 days, with the
   participations by day; with an API the ledger is empty and the 7-day figures say « pas encore mesuré » (never a
   figure of their own). « À faire en priorité » folds readiness()'s to-dos, the drafts and the unread retours privés;
   « Du parcours au salon » reads four independent states — none gates the next.
   Wireframes-fully ruling 20/10 § 1 (campaign-first first run): day one lasts until the first campaign is live; WF-051's one
   primary is « Créer ma première campagne », the builder at step 1 (what the account lacks is asked there, skippable). D-49's six
   steps are no longer the first run: they are the « Guide Démarrer » rows, a door in the header as on every Accueil. After the
   first publication the logo, the colours, the fiche and the other platforms are « À faire en priorité » rows — never a
   condition of anything — and the QR is readiness()'s to-do of the support the publication created, as before. */

/* Batch 2 (02/09) — every row lands on its fix, never a dead number. An item names its screen (`to`);
   older fixtures are routed by their words, because they vary per world; unknown → Résultats. */
function routeFor(a: ActionItem): string {
  if (a.to) return a.to
  const t = (a.title + ' ' + a.action).toLowerCase()
  if (t.includes('roue')) return '/cadeaux?onglet=roues'
  if (t.includes('stock') || t.includes('cadeau')) return '/cadeaux'
  if (t.includes('paiement') || t.includes('carte')) return '/parametres/paiement'
  if (t.includes('invitation')) return '/parametres/equipe'
  if (t.includes('support') || t.includes('scanné')) return '/supports'
  if (t.includes('fiche') || t.includes('établissement') || t.includes('participation depuis')) return '/etablissements'
  if (t.includes('destination')) return '/destinations'
  if (t.includes('campagne')) return '/campagnes'
  return '/resultats'
}

/* the flag the mock keeps once the brand is set (the guide's step 1, Marque) — the colours to-do reads it */
export const identityKey = () => `bmr.identite-ok.${currentWorld()}`

/* ---------- WF-001 · the activity over 7 / 30 days, from the ledger ---------- */
type Periode = 7 | 30
const DAY = 86400e3
const NOT_MEASURED = 'pas encore mesuré'
/** a Paris calendar day as `YYYY-MM-DD` (fr-CA prints ISO order) */
const dayKey = (t: number | string) => new Date(t).toLocaleDateString('fr-CA', { timeZone: 'Europe/Paris' })
const dayLabel = (key: string) => new Date(key + 'T12:00:00Z').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Europe/Paris' })
interface Activity { participations: number; attribues: number; utilises: number; ouvertes: number; days: { key: string; n: number }[] }
/** The window's figures from the ledger rows the view may read (the context's supports when there is one). Every figure is
    a count of the same rows — the CSV-is-the-view rule of Résultats; a test never enters the ledger. */
function activityOf(rows: LedgerRow[], periode: Periode, now: number): Activity {
  /* the window is `periode` Paris calendar days ending today (the last one partial), so the tiles and the chart count the
     same rows — « regroupée par date à Paris » */
  const days: { key: string; n: number }[] = []
  for (let i = periode - 1; i >= 0; i--) days.push({ key: dayKey(now - i * DAY), n: 0 })
  const idx = new Map(days.map((d, i) => [d.key, i]))
  const inWin = (iso: string) => { const t = Date.parse(iso); return t <= now && idx.has(dayKey(t)) }
  const inPeriod = rows.filter(r => inWin(r.at))
  for (const r of inPeriod) days[idx.get(dayKey(r.at))!].n++
  return {
    participations: inPeriod.length,
    attribues: inPeriod.filter(r => !!r.gift).length,
    utilises: rows.filter(r => r.gift?.status === 'utilise' && r.gift.usedAt && inWin(r.gift.usedAt)).length,
    ouvertes: inPeriod.filter(r => !!r.invitation?.opened).length,
    days,
  }
}

/** « Participations au fil des jours » — one series, the brand's action hue, thin bars anchored on the baseline; the table
    under the fold is the same numbers with their definition. */
function ParJour({ days, periode }: { days: { key: string; n: number }[]; periode: Periode }) {
  const total = days.reduce((a, d) => a + d.n, 0)
  const max = Math.max(1, ...days.map(d => d.n))
  return (
    <section className="card" data-testid="par-jour">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'baseline' }}>
        <p className="t-section" style={{ margin: 0 }}>Participations au fil des jours</p>
        <span className="f-help">{total.toLocaleString('fr-FR')} sur les {periode} jours précédents</span>
      </div>
      {total === 0 ? (
        <p className="f-help" style={{ margin: '10px 0 0' }}>Aucune participation sur la période — rien à dessiner encore.</p>
      ) : (
        <div className="par-jour" role="img" aria-label={`${total} participations sur ${periode} jours, par jour`}
          style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120, marginTop: 12, borderBottom: '1px solid var(--bmr-border)' }}>
          {days.map(d => (
            <div key={d.key} title={`${dayLabel(d.key)} · ${d.n}`} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'flex-end', height: '100%' }}>
              <div style={{ width: '100%', height: `${Math.max(d.n ? 3 : 0, (d.n / max) * 100)}%`, background: 'var(--bmr-action)', borderRadius: '4px 4px 0 0' }} />
            </div>
          ))}
        </div>
      )}
      <div className="f-help" style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        <span>{dayLabel(days[0].key)}</span><span>{dayLabel(days[days.length - 1].key)}</span>
      </div>
      <details style={{ marginTop: 10 }}>
        <summary className="f-help" style={{ cursor: 'pointer', fontWeight: 700, color: 'var(--bmr-text)' }}>Voir les chiffres par jour et leur définition</summary>
        <p className="f-help" style={{ margin: '8px 0' }}>Fenêtre glissante de {periode} jours, regroupée par date à Paris. Le premier et le dernier jour peuvent être partiels. Tests exclus. Une participation BMR ne prouve pas un rendez-vous au salon.</p>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
          <thead><tr style={{ textAlign: 'left', color: 'var(--bmr-text-muted)' }}><th style={{ padding: '4px 0' }}>Date (Paris)</th><th style={{ textAlign: 'right' }}>Participations</th></tr></thead>
          <tbody>{days.map(d => <tr key={d.key}><td style={{ padding: '3px 0', borderTop: '1px solid var(--bmr-border)' }}>{dayLabel(d.key)}</td><td style={{ textAlign: 'right', borderTop: '1px solid var(--bmr-border)', fontVariantNumeric: 'tabular-nums' }}>{d.n}</td></tr>)}</tbody>
        </table>
      </details>
    </section>
  )
}

/* MA-3 · the till door, for every role (setup spec question 3, default yes). The till is a page on the
   player host; « Ouvrir la caisse » goes through the « Valider un cadeau » door (/caisse, the sidebar's too), which
   opens it on this device, in this tab, after registering the device when the person can do that in one tap (V19).
   `lastUsedAt` is the till's last use (PIN, lookup or validation), so the line says « utilisation »;
   « dernière validation » waits for a read of the gift ledger (reporting rows). Staff do not read the tills (the API
   answers them 403): their line is the first half alone. */
function TillDoor() {
  const nav = useNavigate()
  const [scope] = useScope()
  const { data: tills, isSuccess } = useTillDevices(currentUser().role !== 'staff')
  const live = (tills ?? []).filter(t => !t.revokedAt && (scope === ALL || t.locationId === scope))
  const last = live.map(t => t.lastUsedAt).filter((x): x is string => !!x).sort().pop()
  const none = isSuccess && live.length === 0
  return (
    <section className="card till-door" data-testid="till-door">
      <div>
        <p className="t-section" style={{ margin: 0 }}>Valider un cadeau</p>
        <p className="f-help">
          {none
            ? <>Ouvre la caisse sur cet appareil. Aucune caisse enregistrée pour l’instant.</>
            : <>Ouvre la caisse sur cet appareil{last ? <> · dernière utilisation {quandParis(last)}</> : '.'}</>}
        </p>
      </div>
      <Button size="sm" onClick={() => nav('/caisse')}>Ouvrir la caisse</Button>
    </section>
  )
}

export default function Accueil() {
  const { data, isPending, isError, refetch } = useOverview()
  const { data: supports } = useSupports()
  const { data: locations } = useLocations()
  const unread = useUnreadMessages()
  const { data: feed } = useNotifications()
  const { data: apiCampaigns } = useCampaigns()
  const [scope] = useScope()
  const nav = useNavigate()
  const [periode, setPeriode] = useState<Periode>(30)
  const [plus, setPlus] = useState(false)
  /* MA-8 · « Du parcours au salon »'s Test reads the test read — the gift validated in mode test, or with no gift the parcours played */
  const published = getCampaigns().find(c => c.version > 0)
  const { data: test } = useCampaignTest(published?.id)

  if (isPending) return (
    <div style={{ display: 'grid', gap: 10, maxWidth: 720 }}>
      <Skeleton width="42%" /><Skeleton /><Skeleton width="65%" />
    </div>
  )
  if (isError) return (
    <EmptyState title="Impossible de charger l’accueil."
      why="Votre connexion a peut-être sauté — rien n’a été perdu."
      action={<Button variant="primary" size="sm" onClick={() => refetch()}>Réessayer</Button>} />
  )

  const d = data
  const name = currentUser().name
  const firstRun = d.checklist !== null
  const ctx = scope !== ALL ? (locations ?? []).find(l => l.id === scope) : undefined
  let flagIdent = false
  try { flagIdent = localStorage.getItem(identityKey()) === '1' } catch { /* fine */ }
  const realSupports = mergeSupports(supports)
  const locId = (locations ?? [])[0]?.id ?? 'l0'
  const lite = allCampaigns(apiCampaigns, Object.fromEntries((locations ?? []).map(l => [l.id, l.name])))
  /* ruling 20/10 § 1 · day one = a first run with no campaign live yet (published here, or en ligne / programmée / en pause on
     the server); the first publication ends it — the QR is then the created support's to-do */
  const liveAny = getCampaigns().some(c => c.version > 0) || lite.some(c => c.status === 'active' || c.status === 'paused' || c.status === 'scheduled')
  const dayOne = firstRun && !liveAny
  /* D-31 · in a context: the establishment's own items (what names it), never invented figures */
  const aTraiter = ctx ? d.aTraiter.filter(a => (a.title + ' ' + a.meta).includes(ctx.name)) : d.aTraiter
  const chose = aTraiter[0]
  /* MA-2 · honest zero: the tiles appear with their source, the first participation */
  const tiles = !dayOne && (d.participations30d > 0 || !!ctx)
  /* N1 · the quota line on the participations tile (contract 11: the facts the bell counts; owner and admin only).
     The pause wins over the 80 % / 100 % warning; the plan's quota is the workspace's, every campaign shares it. */
  const quota = quotaFact(factsFor(feed))
  const pct = typeof quota?.meta?.percent === 'number' ? quota.meta.percent : 80
  const jourQuota = quota ? new Date(quota.at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', timeZone: 'Europe/Paris' }) : ''
  const lien = (label: string, to: string) => <a href="#" onClick={e => { e.preventDefault(); nav(to) }}>{label}</a>
  /* Version 17 (ACCEPTED 10/10): Offre is the owner's; an admin or a manager reads the same line with no door (lib-pack-15) */
  const owner = currentUser().role === 'owner'
  const staff = currentUser().role === 'staff'
  /* Version 19's header line (I-1 vet point 4, lib-pack-15): the establishment in view and its campagne principale — en ligne
     or en pause, as the prototype's `principal()` reads it; none chosen, or programmée / terminée → « Aucune … en ligne ». One
     establishment in view only (the context, or the single one): the network view says nothing here. */
  const enVue = ctx ?? ((locations ?? []).length === 1 ? (locations ?? [])[0] : undefined)
  const principale = enVue ? allCampaigns(apiCampaigns, Object.fromEntries((locations ?? []).map(l => [l.id, l.name]))).find(c => (c.status === 'active' || c.status === 'paused') && (c.principal ?? []).includes(enVue.id)) : undefined
  const ligneEnTete = enVue ? `${enVue.name} · ${principale ? `« ${principale.name} » est votre campagne principale.` : 'Aucune campagne principale en ligne.'}` : undefined
  /* v3.3 WF-051 (gap-map vet § 4): « À faire en priorité » = readiness()'s five states, as to-dos, for the supports in view —
     never a second model, never a gate. One line per support still short of a state, its first missing state; the support's
     page is the door. The section disappears when every support is ready (or there is none yet). */
  const supportsInView = realSupports.filter(s => s.kind !== 'personnel' && (scope === ALL || s.locationId === scope))
  const aFaire = supportsInView
    .map(s => ({ s, todo: readiness(s, servedBy(s, lite).current).filter(x => !x.done) })).filter(x => x.todo.length > 0).slice(0, 5)
  /* WF-001 · the other to-dos of the reference: a draft to finish (this browser's own, never published), the retours privés
     to read. Rows, each with its door; three shown, the rest under « Voir les N autres actions ». */
  const brouillons = staff ? [] : getCampaigns().filter(c => c.version === 0 && c.status === 'draft' && (scope === ALL || c.locations.includes(scope)))
  type Row = { key: string; title: string; meta: string; door: string; to: string }
  /* ruling 20/10 § 1 · what the builder did not need stays a to-do of the first run, after the first publication — never a
     condition: the fiche (when step 1's card was passed), the logo, the colours (the brand never set: the guide's step 1 or
     Marque sets the flag), the platforms beyond Google. Each row's door is the page that does it. */
  const valid = getDestinations().filter(x => x.locationId === locId && x.state === 'validee')
  const setup: Row[] = firstRun && !staff && !ctx ? [
    ...(!getFiche() && !valid.some(x => x.platform === 'google') ? [{ key: 'fiche', title: 'Votre fiche Google', meta: '· le nom, l’adresse et les horaires viennent de la fiche', door: 'Lier', to: '/guide?etape=1' }] : []),
    ...(!getWheelDesign().logoUrl ? [{ key: 'logo', title: 'Votre logo', meta: '· au centre de la roue et en haut de chaque écran', door: 'Ajouter', to: '/identite' }] : []),
    ...(!flagIdent ? [{ key: 'couleurs', title: 'Vos couleurs', meta: '· sur chaque écran du client', door: 'Choisir', to: '/identite' }] : []),
    ...(!valid.some(x => x.platform !== 'google') ? [{ key: 'plateformes', title: 'Vos autres plateformes', meta: '· Pages Jaunes, Facebook, Trustpilot… — facultatives', door: 'Ajouter', to: '/parametres/plateformes' }] : []),
  ] : []
  const rows: Row[] = [
    ...aFaire.map(({ s, todo }): Row => ({ key: `s-${s.id}`, title: s.name, meta: `· ${todo[0].label.toLowerCase()}${todo.length > 1 ? ` · ${todo.length - 1} autre${todo.length > 2 ? 's' : ''} point${todo.length > 2 ? 's' : ''}` : ''}`, door: 'Ouvrir le support', to: `/supports?vue=detail&id=${s.id}` })),
    ...setup,
    ...brouillons.map((c): Row => ({ key: `c-${c.id}`, title: `Terminer « ${c.name || 'Sans nom'} »`, meta: 'Brouillon enregistré, non diffusé.', door: 'Ouvrir', to: `/campagnes/nouvelle?brouillon=${c.id}` })),
    ...(unread > 0 && !ctx ? [{ key: 'retours', title: `${unread} retour${unread > 1 ? 's' : ''} privé${unread > 1 ? 's' : ''} à lire`, meta: 'Lus par vous seulement — jamais un avis.', door: 'Ouvrir', to: '/messages?onglet=retours' } as Row] : []),
  ]
  const shown = plus ? rows : rows.slice(0, 3)
  const reste = rows.length - 3
  /* WF-001 · « Activité du salon »: the ledger's rows the view may read (the context's supports in a context); empty with an
     API, where the 30-day figures are the overview's and the 7-day ones are « pas encore mesuré » */
  const now = Date.now()
  const ledger = getLedger()
  const ledgerRows = ctx ? ledger.filter(r => r.supportId !== null && supportsInView.some(s => s.id === r.supportId)) : ledger
  const act = ledger.length ? activityOf(ledgerRows, periode, now) : null
  const fig = (ledgerN: number | undefined, overview30: number): number | string =>
    act ? (ledgerN ?? 0) : periode === 30 && !ctx ? overview30 : NOT_MEASURED
  /* WF-001 · « Du parcours au salon »: four states read independently — a test is guidance, a missing state hides nothing */
  const etats = [
    { label: 'Campagne', done: getCampaigns().some(c => c.version > 0) || lite.some(c => c.status === 'active' || c.status === 'paused' || c.status === 'scheduled') },
    { label: 'Test', done: !!published && (!!published.testedAt || testDone(test, !campaignHasGift(published.recipe))) },
    { label: 'Support', done: supportsInView.length > 0 },
    { label: 'Mise en place', done: supportsInView.some(s => readiness(s, servedBy(s, lite).current).find(x => x.key === 'placement')?.done) },
  ]

  /* staff validates gifts; the setup is not theirs to do (D-46): their day one is the till door */
  if (dayOne && staff) return (
    <div className="page accueil">
      <PageHeader title={`Bienvenue${name ? `, ${name}` : ''}.`} />
      <TillDoor />
    </div>
  )
  if (dayOne) return (
    <div className="page accueil">
      {/* v3.3 WF-051 · the reference's welcome; ruling 20/10 § 1 · campaign-first: the one primary opens the builder at step 1,
          D-49's guide stays one door away (« Guide Démarrer », as on every Accueil) */}
      <p className="t-eyebrow" data-testid="bienvenue-bmr">Bienvenue chez BMR{name ? `, ${name}` : ''}</p>
      <PageHeader title="Préparez votre premier parcours."
        description="Du parcours client au support utilisable dans votre salon."
        actions={<Button variant="quiet" size="sm" data-testid="guide-demarrer" onClick={() => nav('/guide?etape=1')}>Guide Démarrer</Button>} />

      <section className="card next-step" data-testid="next-step">
        <h2 className="t-section" style={{ margin: 0 }}>Votre prochaine étape</h2>
        <p className="t-body">Choisissez une visite simple ou plusieurs visites. Vous pourrez ensuite créer vos cadeaux, tester le parcours et préparer le QR.</p>
        <div className="next-step-actions">
          <Button variant="primary" data-testid="creer-premiere-campagne" onClick={() => nav('/campagnes/nouvelle?reset=1')}>Créer ma première campagne</Button>
          {/* a draft kept with « Enregistrer et quitter » reopens where it was — the primary still starts a new one */}
          {brouillons[0] && <Button size="sm" data-testid="reprendre-brouillon" onClick={() => nav(`/campagnes/nouvelle?brouillon=${brouillons[0].id}`)}>Reprendre « {brouillons[0].name || 'Sans nom'} »</Button>}
          <a href="#" onClick={e => { e.preventDefault(); window.open('/d/demo', '_blank') }}>Voir le parcours côté client</a>
        </div>
        {/* the road in three lines — sequence copy, never a gate: the QR shows as soon as a campaign is live */}
        <ol className="t-body" style={{ margin: '6px 0 0', paddingLeft: 20 }} data-testid="chemin-3">
          <li>Créer le parcours.</li>
          <li>Tester et publier.</li>
          <li>Préparer et vérifier le support au salon.</li>
        </ol>
      </section>

      <Note tone="candour">Votre activité apparaîtra ici après les premières participations. Les tests ne sont jamais comptés.</Note>

      <TillDoor />
    </div>
  )

  return (
    <div className="page accueil">
      {/* v3.3 WF-051 (gap-map vet § 4): « Guide Démarrer » reopens D-49's six-step guide — the first run stays the one setup */}
      <PageHeader title={ctx ? ctx.name : `Bonjour${name ? `, ${name}` : ''}.`}
        description={<>L’activité du salon, puis les actions à suivre.{ligneEnTete && <><br /><span data-testid="principale-line">{ligneEnTete}</span></>}</>}
        actions={!staff && <Button variant="quiet" size="sm" data-testid="guide-demarrer" onClick={() => nav('/guide?etape=1')}>Guide Démarrer</Button>} />

      {/* one thing to do this week — the most consequential; the section disappears when nothing waits
          (never « all good ») */}
      {chose ? (
        <section className="card next-step" data-testid="une-chose">
          <p className="t-eyebrow">Une chose à faire cette semaine</p>
          <p className="t-section" style={{ margin: 0 }}>{chose.title}</p>
          <p className="f-help">{chose.meta}</p>
          <div className="next-step-actions">
            <Button variant="primary" size="sm" onClick={() => nav(routeFor(chose))}>{chose.action}</Button>
          </div>
        </section>
      ) : firstRun && !ctx && rows.length === 0 && (
        <Note tone="ok"><b>Tout est en place — vos clients peuvent jouer.</b> Les premiers résultats apparaîtront ici&nbsp;; d’ici là, posez le support là où le client le voit.</Note>
      )}

      {/* four real numbers — never a review counted. The demo line is Résultats' own (product-audits
          2.27, demo-banner vet § 3): the ledger is the demo's until the reporting rows; delete with T4.2. */}
      {tiles && (
        <>
          <DemoBanner />
          <section style={{ display: 'grid', gap: 10 }} data-testid="activite">
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px' }}>
                <p className="t-eyebrow" data-testid="perimetre-ligne">{ctx ? ctx.name : 'Tout l’établissement'} · toutes les campagnes · hors tests</p>
                <p className="t-section" style={{ margin: '2px 0 0' }}>Activité du salon</p>
              </div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <Segmented value={String(periode)} onChange={v => setPeriode(v === '7' ? 7 : 30)} options={[['7', '7 jours'], ['30', '30 jours']]} />
                <Button size="sm" onClick={() => nav(periode === 7 ? '/resultats?periode=7' : '/resultats')}>Tous les résultats</Button>
              </div>
            </div>
            <Tiles>
              <Tile label={`Participations · ${periode} j`} value={fig(act?.participations, d.participations30d)}
                hint={quota?.kind === 'quota_paused' ? <span data-testid="quota-line">quota atteint · en pause depuis le {jourQuota}{owner && <> · {lien('voir mon offre', '/parametres/offre')}</>}</span>
                  : quota ? <span data-testid="quota-line">{pct >= 100 ? 'quota atteint · la marge de 20 % court' : `${pct} % du quota du mois`}{owner && <> · {lien('voir l’utilisation', '/parametres/utilisation')}</>}</span>
                  : 'parcours démarrés · jamais un test'} />
              <Tile label={`Cadeaux attribués · ${periode} j`} value={act ? act.attribues : NOT_MEASURED} hint="gagnés sur la période · décidés par la roue" />
              <Tile label={`Cadeaux utilisés · ${periode} j`} value={fig(act?.utilises, d.giftsRedeemed30d)} hint="validés à la caisse" />
              <Tile label={`Invitations ouvertes · ${periode} j`} value={fig(act?.ouvertes, d.invitationsOpened30d)} hint="avis et réseaux · publication non confirmée · jamais un avis compté" />
            </Tiles>
            {act ? <ParJour days={act.days} periode={periode} /> : (
              <section className="card" data-testid="par-jour">
                <p className="t-section" style={{ margin: 0 }}>Participations au fil des jours</p>
                <p className="f-help" style={{ margin: '6px 0 0' }}>Par jour&nbsp;: {NOT_MEASURED}. Les chiffres par jour arrivent avec les résultats détaillés&nbsp;; d’ici là, {lien('tous les résultats', '/resultats')}.</p>
              </section>
            )}
          </section>
        </>
      )}

      {rows.length > 0 && (
        <section className="card" data-testid="a-faire">
          <p className="t-eyebrow">À faire en priorité</p>
          <ul className="todo-list" style={{ margin: 0, padding: 0, listStyle: 'none', display: 'grid', gap: 8 }}>
            {shown.map(r => (
              <li key={r.key} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span className="t-section" style={{ margin: 0 }}>{r.title}</span>
                <span className="f-help">{r.meta}</span>
                <a href="#" style={{ marginLeft: 'auto' }} onClick={e => { e.preventDefault(); nav(r.to) }}>{r.door}</a>
              </li>
            ))}
          </ul>
          {reste > 0 && !plus && <p style={{ margin: '10px 0 0' }}><a href="#" data-testid="a-faire-plus" onClick={e => { e.preventDefault(); setPlus(true) }}>Voir {reste === 1 ? 'l’autre action' : `les ${reste} autres actions`}</a></p>}
        </section>
      )}

      {/* success moment — once, never repeated (ON-14…17) */}
      {d.successMoment && !ctx && (
        <section className="card">
          <p className="t-section" style={{ marginBottom: 6 }}>{d.successMoment.title}</p>
          <p className="t-body">{d.successMoment.body}</p>
          <div style={{ marginTop: 12 }}>
            <Button variant="quiet" size="sm">{d.successMoment.action}</Button>
          </div>
        </section>
      )}

      {/* WF-001 · the road from the campaign to the salon, four states side by side (none gates the next), three doors */}
      {!staff && (
        <section className="card" data-testid="parcours-salon" style={{ display: 'grid', gap: 10 }}>
          <p className="t-section" style={{ margin: 0 }}>Du parcours au salon</p>
          <ol className="chemin" style={{ display: 'flex', gap: '4px 10px', flexWrap: 'wrap', alignItems: 'center', margin: 0, padding: 0, listStyle: 'none' }}>
            {etats.map((e, i) => (
              <li key={e.label} style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                <span aria-hidden style={{ display: 'inline-flex', width: 18, height: 18, borderRadius: '50%', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 800, background: e.done ? 'var(--bmr-selected)' : 'var(--bmr-surface-sunken)', color: e.done ? 'var(--bmr-action)' : 'var(--bmr-text-muted)' }}>{e.done ? '✓' : '○'}</span>
                <span className="t-body" style={{ margin: 0 }}>{e.label}<span className="f-help"> · {e.done ? 'fait' : 'à faire'}</span></span>
                <span aria-hidden className="f-help">{i < etats.length - 1 ? '→' : '→ Résultats'}</span>
              </li>
            ))}
          </ol>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="primary" size="sm" onClick={() => nav('/supports')}>Préparer mes supports</Button>
            <Button size="sm" onClick={() => nav('/equipe')}>Retrouver les QR de l’équipe</Button>
            <Button size="sm" onClick={() => nav('/resultats?vue=definitions')}>Comprendre mes résultats</Button>
          </div>
        </section>
      )}

      <TillDoor />

      {ctx && (locations?.length ?? 0) > 1 && (
        <p className="f-help" style={{ margin: 0 }}><a href="#" onClick={e => { e.preventDefault(); nav('/etablissements/comparer?etablissement=all') }}>Comparer aux autres établissements</a> · <a href="#" onClick={e => { e.preventDefault(); nav('/?etablissement=all') }}>Tous les établissements</a></p>
      )}
    </div>
  )
}
